import { cx } from '@linaria/core';
import { useRef, Suspense, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2Header } from '@pm2/design-tokens';
import { useLocation } from '@provider/navigation';
import { SizeTransition, useSelf } from '@utils/react';
import { Routes } from '../const.routes.js';
import { FontPane } from '../features/settings/molecule.font/_page.js';
import { MyTeamsPane } from '../features/settings/molecule.my-teams/_page.js';
import { SpeechSynthesisPane } from '../features/settings/molecule.speech-synthesis/_page.js';
import { Status } from '../features/status/components/page.js';
import { getNavHeight } from '../utils/getNavHeight.js';
import { wrapperCls, routeTransitionCls, ROUTE_TRANSITION_DURATION_MS, contentCls } from './content.style.js';
import { jsx, Fragment } from 'react/jsx-runtime';
import { CourseBuilderPage } from '../features/courses/page.js';
import { EntriesPage } from '../features/component-entries/page.js';
import { Portals } from '../features/portals/components/page.js';

const Content = () => {
  const contentRef = useRef(null);
  const location = useLocation();
  const currentRoute = useActiveRoute(contentRef);
  useRouteChange(contentRef);
  return /*#__PURE__*/jsx("section", {
    ref: contentRef,
    className: cx(wrapperCls, location.pathname !== "/" && "atContent"),
    children: /*#__PURE__*/jsx(Suspense, {
      fallback: null,
      children: /*#__PURE__*/jsx(TransitionGroup, {
        component: null,
        children: /*#__PURE__*/jsx(SizeTransition, {
          classNames: routeTransitionCls,
          timeout: ROUTE_TRANSITION_DURATION_MS,
          axis: "vertical",
          swappable: true,
          getMinSizeInPx: () => ({
            height: getNavHeight()
          }),
          getMaxSizeInPx: () => ({
            height: window.innerHeight - pm2Header.getCurrentHeight() + 25
          }),
          children: ref => location.pathname === "/" ? /*#__PURE__*/jsx("div", {
            className: routeTransitionCls,
            ref: ref
          }) : /*#__PURE__*/jsx("div", {
            ref: ref,
            className: cx(contentCls, routeTransitionCls),
            children: currentRoute
          })
        }, location.pathname)
      })
    })
  });
};
function useActiveRoute(contentRef) {
  const location = useLocation();
  switch (location.pathname) {
    case Routes.PORTALS:
      return /*#__PURE__*/jsx(Portals, {});
    case Routes.STATUS:
      return /*#__PURE__*/jsx(Status, {
        wrapperRef: contentRef
      });
    case Routes.ENTRIES:
      return /*#__PURE__*/jsx(EntriesPage, {});
    case Routes.COURSES:
      return /*#__PURE__*/jsx(CourseBuilderPage, {
        scrollerRef: contentRef
      });
    case Routes.TEAMS:
      return /*#__PURE__*/jsx(MyTeamsPane, {});
    case Routes.SPEECH:
      return /*#__PURE__*/jsx(SpeechSynthesisPane, {});
    case Routes.FONT:
      return /*#__PURE__*/jsx(FontPane, {});
    default:
      return /*#__PURE__*/jsx(Fragment, {});
  }
}
function useRouteChange(contentRef) {
  const location = useLocation();
  const self = useSelf({
    prevRoute: location.pathname
  });
  useEffect(() => {
    if (location.pathname === self.prevRoute) {
      return;
    }
    const content = contentRef.current;
    if (content) {
      // Ensure that the top of the new page is always visible, no matter how
      // far the user has been scrolling on previous pages
      content.scrollTo(0, 0);
    }
    return () => {
      self.prevRoute = location.pathname;
    };
  }, [self, location.pathname, contentRef]);
}

export { Content };
//# sourceMappingURL=content.js.map
